// src/utils/secureApi.js
import { getCurrentUser, validateUserOwnership, handleSessionExpiry } from './auth';

class SecureApiError extends Error {
  constructor(message, code = 'API_ERROR') {
    super(message);
    this.name = 'SecureApiError';
    this.code = code;
  }
}

// Secure fetch wrapper with automatic user validation
export const secureApiFetch = async (url, options = {}, navigate = null) => {
  const currentUser = getCurrentUser();
  
  if (!currentUser) {
    console.error('[SecureApi] No authenticated user found');
    if (navigate) {
      handleSessionExpiry(navigate, 'Authentication required');
    }
    throw new SecureApiError('User not authenticated', 'AUTH_REQUIRED');
  }

  // Add authentication headers
  const secureOptions = {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${currentUser.token}`,
      'X-User-ID': currentUser.userId,
      ...options.headers
    }
  };

  try {
    const response = await fetch(url, secureOptions);
    
    if (response.status === 401) {
      console.error('[SecureApi] Session expired');
      if (navigate) {
        handleSessionExpiry(navigate, 'Session expired');
      }
      throw new SecureApiError('Session expired', 'SESSION_EXPIRED');
    }

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[SecureApi] Error response:', {
        status: response.status,
        statusText: response.statusText,
        body: errorText
      });
      throw new SecureApiError(
        `API Error: ${response.status} - ${errorText}`,
        'HTTP_ERROR'
      );
    }

    const data = await response.json();
    
    // Validate user ownership for user-specific data
    if (data.userId && !validateUserOwnership(data.userId)) {
      console.error('[SecureApi] Data ownership validation failed');
      throw new SecureApiError('Data ownership validation failed', 'OWNERSHIP_ERROR');
    }

    return data;
  } catch (error) {
    console.error('[SecureApi] Request failed:', {
      url,
      error: error.message,
      code: error.code || 'UNKNOWN'
    });

    if (error instanceof SecureApiError) {
      throw error;
    }
    throw new SecureApiError(
      `Network or server error: ${error.message}`,
      'NETWORK_ERROR'
    );
  }
};

// Secure user data operations
export const secureUserOperations = {
  // Get user profile with validation
  getProfile: async (navigate) => {
    const currentUser = getCurrentUser();
    if (!currentUser) throw new SecureApiError('No authenticated user', 'AUTH_REQUIRED');
    
    const url = `${process.env.REACT_APP_API_BASE_URL}/api/users/${currentUser.userId}`;
    return await secureApiFetch(url, { method: 'GET' }, navigate);
  },

  // Update password with validation
  changePassword: async (currentPassword, newPassword, navigate) => {
    const currentUser = getCurrentUser();
    if (!currentUser) throw new SecureApiError('No authenticated user', 'AUTH_REQUIRED');
    
    const url = `${process.env.REACT_APP_API_BASE_URL}/api/users/change-password`;
    return await secureApiFetch(url, {
      method: 'POST',
      body: JSON.stringify({
        userId: currentUser.userId,
        currentPassword,
        newPassword
      })
    }, navigate);
  },

  // Get user-specific data with validation
  getUserData: async (endpoint, navigate) => {
    const currentUser = getCurrentUser();
    if (!currentUser) throw new SecureApiError('No authenticated user', 'AUTH_REQUIRED');
    
    const url = `${process.env.REACT_APP_API_BASE_URL}${endpoint}`.replace(':userId', currentUser.userId);
    return await secureApiFetch(url, { method: 'GET' }, navigate);
  }
};

export { SecureApiError };
